import { IBackStory } from "@src/components/Backstory";
import image from "../../../../public/images/t3-demo-1.jpg";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import DialogModal from "@src/components/Dialog";
import BackstoryModal from "@src/components/modals/BackstoryModal";
import { truncate } from "lodash";
import { getRole } from "@src/utils/general";

const Backstory: React.FC<IBackStory> = ({ eventId, backStoryData }) => {
  const text = "The Journey to 'I Do'";
  const [displayed, setDisplayed] = useState("");
  const [typing, setTyping] = useState(true);
  const [inView, setInView] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const containerRef = useRef(null);

  const stories = backStoryData?.getBackStory ?? [];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inView) {
          setInView(true);
        }
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [inView]);

  useEffect(() => {
    if (!inView) return;

    setDisplayed("");
    setTyping(true);
    let i = 0;
    const interval = setInterval(() => {
      setDisplayed((prev) => prev + text[i]);
      i++;

      if (i >= text.length) {
        clearInterval(interval);
        setTyping(false);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [inView]);

  // Auto-advance slides
  useEffect(() => {
    if (stories.length <= 1 || isModalOpen) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % stories.length);
        setIsAnimating(false);
      }, 300);
    }, 10000); // 10 seconds per slide

    return () => clearInterval(interval);
  }, [stories.length, isModalOpen]);

  const handleSlideChange = (index: number) => {
    if (index === currentSlide || isAnimating) return;

    setIsAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setIsAnimating(false);
    }, 300);
  };

  const currentStory = stories[currentSlide];

  return stories.length > 0 ? (
    <section className="w-full pb-16 pt-10 sm:pt-16 bg-[#FEFEFE]">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div ref={containerRef} className="text-center mb-8 lg:mb-12">
          <h2 className="font-dacing text-4xl md:text-7xl font-light text-[#4A2C47]">
            {displayed}
            {typing && <span className="animate-pulse">|</span>}
          </h2>
        </div>

        {/* Single Story Slide */}
        {currentStory && (
          <div
            className={`transition-all duration-500 ${
              isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
            }`}
          >
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
              {/* Left Column - Image */}
              <div className="w-full lg:w-1/2 flex justify-center">
                <div className="w-60 h-72 lg:w-96 lg:h-[480px] rounded-lg overflow-hidden bg-gray-300 relative shadow-2xl">
                  <Image
                    placeholder="blur"
                    loading="eager"
                    layout="fill"
                    blurDataURL={`data:image/svg+xml;base64,${toBase64(
                      shimmer(700, 475)
                    )}`}
                    src={currentStory?.image || image}
                    alt={currentStory?.title || "Wedding photo"}
                    className="object-cover"
                    sizes="(max-width: 768px) 320px, 384px"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>
              </div>

              <div className="w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left">
                <h3 className="text-[#4A2C47] text-2xl lg:text-4xl font-normal tracking-widest mb-4">
                  {currentStory?.title || "Our Story"}
                </h3>

                <p className="text-[#4A2C47] text-base lg:text-lg font-normal text-justify font-corGaramond leading-relaxed mb-6 max-w-md">
                  {truncate(currentStory?.story, {
                    length: 300,
                  }) || "Our love story begins here..."}
                </p>
                <p className="text-[#4A2C47] text-sm text-sm font-normal tracking-widest mb-4">
                  {getRole(currentStory?.role)}
                </p>

                <DialogModal
                  trigger={
                    <button className="border border-[#4A2C47] text-[#4A2C47] px-8 py-3 text-sm tracking-wider font-ebGaramond hover:bg-[#4A2C47] hover:text-[#FEFEFE] transition-colors duration-300 rounded-2xl transform hover:scale-105">
                      CONTINUE READING
                    </button>
                  }
                  onOpenChange={setIsModalOpen}
                >
                  <BackstoryModal
                    title={currentStory?.title ?? ""}
                    story={currentStory?.story ?? ""}
                    role={getRole(currentStory?.role) ?? ""}
                  />
                </DialogModal>
              </div>
            </div>
          </div>
        )}

        {stories.length > 1 && (
          <div className="flex justify-center gap-3 mt-12">
            {stories.map((_, index) => (
              <button
                key={index}
                onClick={() => handleSlideChange(index)}
                className={`w-3 h-3 mt-[-2rem] rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "bg-[#4A2C47] scale-125 shadow-lg"
                    : "bg-[#4A2C47]/40 hover:bg-[#4A2C47]/70"
                }`}
                aria-label={`View story ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  ) : (
    <></>
  );
};

export default Backstory;
